<template>
    <v-stage :config="stageConfig">
        <v-layer>
            <!-- Border Rectangle -->
            <v-rect
                :config="borderConfig"
            />

            <!-- Logo Rectangle with Transformer -->
            <v-rect
                ref="logo"
                :config="logoConfig"
                @dragmove="onDragMove"
                @transform="onTransform"
            />
            <v-transformer
                ref="transformer"
                :config="transformerConfig"
            />
        </v-layer>
    </v-stage>
</template>

<script>
import { defineComponent, ref, onMounted } from "vue";
import Konva from "konva";

export default defineComponent({
    name: "LogoWithBorder",
    data() {
        return {
            stageConfig: {
                width: 800,
                height: 600,
            },
            borderConfig: {
                x: 50,
                y: 50,
                width: 700,
                height: 500,
                stroke: "blue",
                strokeWidth: 4,
                listening: false,
            },
            logoConfig: {
                x: 200,
                y: 150,
                width: 200,
                height: 100,
                fill: "red",
                stroke: "black",
                strokeWidth: 2,
                draggable: true,
            },
            transformerConfig: {
                rotateEnabled: true,
                resizeEnabled: true,
                keepRatio: true,
            },
        };
    },
    methods: {
        onDragMove(e) {
            const rect = e.target;
            this.constrainToBorder(rect);
        },
        onTransform(e) {
            const rect = e.target;
            this.preventInvalidTransform(rect);
        },
        preventInvalidTransform(rect) {
            const border = this.borderConfig;
            const padding = 4; // Stroke width of the border
            const box = rect.getClientRect();

            const scaleX = rect.scaleX();
            const scaleY = rect.scaleY();
            const rotation = rect.rotation();
            const oldScaleX = rect.oldScaleX || scaleX;
            const oldScaleY = rect.oldScaleY || scaleY;
            const oldRotation = rect.oldRotation || rotation;

            // Check if transform causes logo to go out of bounds
            if (box.x < border.x + padding ||
                box.y < border.y + padding ||
                box.x + box.width > border.x + border.width - padding ||
                box.y + box.height > border.y + border.height - padding) {
                // Revert to old values if out of bounds
                rect.scaleX(oldScaleX);
                rect.scaleY(oldScaleY);
                rect.rotation(oldRotation);
            } else {
                // Save valid transform values
                rect.oldScaleX = scaleX;
                rect.oldScaleY = scaleY;
                rect.oldRotation = rotation;
            }

            // Prevent position jumping
            const adjustedBox = rect.getClientRect();
            if (adjustedBox.x < border.x + padding ||
                adjustedBox.y < border.y + padding ||
                adjustedBox.x + adjustedBox.width > border.x + border.width - padding ||
                adjustedBox.y + adjustedBox.height > border.y + border.height - padding) {
                rect.position({
                    x: rect.oldX || rect.x(),
                    y: rect.oldY || rect.y(),
                });
            } else {
                rect.oldX = rect.x();
                rect.oldY = rect.y();
            }
        },
        constrainToBorder(rect) {
            const border = this.borderConfig;
            const padding = 4; // Stroke width of the border

            // Get the bounding box including rotation
            const box = rect.getClientRect();

            // Adjust position if the object goes out of bounds
            if (box.x < border.x + padding) {
                rect.x(rect.x() + (border.x + padding - box.x));
            }
            if (box.y < border.y + padding) {
                rect.y(rect.y() + (border.y + padding - box.y));
            }
            if (box.x + box.width > border.x + border.width - padding) {
                rect.x(rect.x() - (box.x + box.width - (border.x + border.width - padding)));
            }
            if (box.y + box.height > border.y + border.height - padding) {
                rect.y(rect.y() - (box.y + box.height - (border.y + border.height - padding)));
            }
        },
    },
    mounted() {
        const logoNode = this.$refs.logo.getNode();
        const transformerNode = this.$refs.transformer.getNode();

        transformerNode.nodes([logoNode]);
        transformerNode.getLayer().batchDraw();
    },
});
</script>

<style scoped>
body {
    margin: 0;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: #f0f0f0;
}
</style>
